import { Autocomplete } from "@material-ui/lab";
import React, { ReactElement, useState } from "react";
import { getOccupationSuggestions } from "../../../../compare/api/api";
import { IDropdownOption } from "../../../interfaces/IDropdownOption";
import cx from "classnames";

//Styles. Currently piggybacking the Input styles
import styles from "./Occupation.module.scss";
import OccupationClassModal from "./OccupationClassModal/OccupationClassModal";
import ValidationMessage from "../ValidationMessage/ValidationMessage";
import useIsMounted from "../../../hooks/useIsMounted";
import { makeStyles } from "@material-ui/core";
import useErrorLogger from "../../../hooks/useErrorLogger";

const UNKNOWN = "Can't find your occupation?";
const UNKNOWN_OPTION: IDropdownOption = {
    label: UNKNOWN,
    value: ""
};

interface IOccupationInputProps {
    id?: string;
    title?: string;
    placeholder?: string;

    initialValue: IDropdownOption;

    onSelect?: (option: IDropdownOption) => void;
    onBlur?: (e: React.ChangeEvent<HTMLDivElement>) => void;

    className?: string;
    hasError?: string | boolean;
}

const useStyles = (): (() => Record<"listbox", string>) =>
    makeStyles(() => ({
        listbox: {
            "paddingBottom": 0,
            "& > *:last-child": {
                backgroundColor: "#ffc0cb"
            }
        }
    }));

const Occupation: React.FC<IOccupationInputProps> = (props): ReactElement => {
    const {
        id,
        title,
        initialValue,
        placeholder,
        onSelect,
        onBlur,
        className,
        children,
        hasError
    } = props;

    const classes = useStyles()();

    const ErrorLogger = useErrorLogger();

    const [isOpenOccupationClassModal, setIsOpenOccupationClassModal] = useState(false);
    const [occupationOptions, setOccupationOptions] = useState([
        initialValue
    ] as IDropdownOption[]);
    const [isLoading, setIsLoading] = useState(false);
    const isMounted = useIsMounted();

    const handleOccupationOptions = (queryString: string): void => {
        setIsLoading(true);
        getOccupationSuggestions(queryString)
            .then((res) => {
                setOccupationOptions([
                    ...res.map((i) => ({
                        label: i.occupation,
                        value: i.class
                    })),
                    UNKNOWN_OPTION
                ]);
            })
            .catch((err) => {
                ErrorLogger.Log(err, {
                    source: "Occupation"
                });
                setOccupationOptions([]);
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    };

    return (
        <div
            className={cx(styles["input-container"], styles["occupation"], "gap-m", {
                [className]: !!className
            })}>
            <OccupationClassModal
                title={initialValue.label}
                isOpen={isOpenOccupationClassModal}
                onClose={async (
                    occName: string | null,
                    occClass: string | number | null
                ) => {
                    if (occClass !== null && occClass !== null) {
                        const occ: IDropdownOption = {
                            label: occName,
                            value: occClass.toString()
                        };
                        await getOccupationSuggestions(occ.label || "a").then((res) => {
                            setOccupationOptions([
                                ...res.map((i) => ({
                                    label: i.occupation,
                                    value: i.class
                                })),
                                UNKNOWN_OPTION
                            ]);

                            onSelect(occ);
                        });
                    }

                    setIsOpenOccupationClassModal(false);
                }}
            />
            <p
                className={cx(
                    { [styles["inputwrapper--error-title"]]: hasError },
                    "text-bold",
                    "text-font-size-s",
                    "text-zero-margin"
                )}>
                {title || "Occupation"}
            </p>
            <div
                className={cx(
                    styles["inputwrapper"],
                    styles["inputwrapper--lightgreen"]
                )}>
                <Autocomplete
                    // Set up
                    id={id}
                    classes={classes}
                    options={[...occupationOptions, UNKNOWN_OPTION]}
                    getOptionLabel={(option) => option.label}
                    inputValue={initialValue.label || ""}
                    // Triggers
                    onInputChange={(e, val) => {
                        if (!e) {
                            return;
                        }

                        if (val && val !== UNKNOWN) {
                            handleOccupationOptions(val);
                        } else if (val === UNKNOWN) {
                            setIsOpenOccupationClassModal(true);
                        }

                        const existingOption = occupationOptions?.find(
                            (i) => i.label === val
                        );

                        if (val !== UNKNOWN) {
                            onSelect({
                                label: val || "",
                                value: existingOption?.value || ""
                            });
                        }
                    }}
                    onChange={(e, val) => {
                        if (val && typeof val !== "string" && val.label === UNKNOWN) {
                            setIsOpenOccupationClassModal(true);
                        } else if (typeof val !== "string") {
                            onSelect(
                                val || {
                                    label: "",
                                    value: ""
                                }
                            );
                        }
                    }}
                    onBlur={(event) => {
                        onBlur(event);
                    }}
                    onFocus={() => {
                        handleOccupationOptions(initialValue.label || "a");
                    }}
                    getOptionSelected={(option, value) => {
                        return option.label === value.label || option.label === UNKNOWN;
                    }}
                    filterOptions={(opt) => {
                        //Ensure that the unknown item is at the end. Cap number of items @5 + Unknown
                        return [
                            ...opt.filter((item) => item.label !== UNKNOWN).splice(0, 5),
                            UNKNOWN_OPTION
                        ];
                    }}
                    //Display Element
                    renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                            <input
                                type={"text"}
                                placeholder={placeholder}
                                {...params.inputProps}
                                className={cx({
                                    [styles["inputwrapper-textinput"]]: true,
                                    [styles["inputwrapper--error"]]: hasError
                                })}></input>
                        </div>
                    )}
                    // Settings
                    fullWidth
                    autoHighlight
                    selectOnFocus
                    freeSolo
                    loading={isLoading}
                />
            </div>
            {hasError && typeof hasError === "string" && (
                <ValidationMessage>{hasError}</ValidationMessage>
            )}
            {children}
        </div>
    );
};

export default Occupation;
